import React from 'react';
import { Link } from 'react-router-dom';
import Avatar from '../common/display/Avatar';
import { Button } from '../common/ui/Button';
import Pill from '../common/display/Pill';
import BaseCard from '../common/layout/BaseCard';
import { CommunityMember } from './types';
import { BoltIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { captureCustomEvent } from '../../utils/posthogUtils';

interface CommunityCardProps {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  members: CommunityMember[];
  admins?: CommunityMember[];
  highlighted?: boolean;
  workspace?: {
    workspace_id?: string;
    workspace_name: string;
    logo_url?: string;
  };
  onViewCommunity?: () => void;
}

export const CommunityCard: React.FC<CommunityCardProps> = ({
  id,
  name,
  description,
  imageUrl,
  members,
  highlighted,
  workspace,
  onViewCommunity,
}) => {
  const maxVisibleAvatars = 5;

  // Filter for actual members and recalculate totalMembers
  const actualMembers = members.filter(member => member.role === 'member');
  const totalActualMembers = actualMembers.length;

  const displayedMembers = actualMembers.slice(0, maxVisibleAvatars);
  const remainingMembers = totalActualMembers - displayedMembers.length;

  const handleViewCommunity = () => {
    captureCustomEvent('community_card_view_clicked', {
      eventProperties: {
        community_id: id,
        community_name: name,
        is_highlighted: highlighted,
        total_members: totalActualMembers,
        has_workspace: !!workspace,
        source: 'community_card'
      }
    });
    
    onViewCommunity?.();
  };

  return (
    <div className="block h-full">
      <BaseCard className={clsx(
        "group",
        highlighted ? "bg-gray-900 border-gray-900" : ""
      )}>
        <div className="flex flex-col h-full relative">
          {imageUrl && (
            <div className={clsx(
              "relative overflow-hidden",
              highlighted ? "h-32 rounded-t-none -mx-7 -mt-7" : "h-32 rounded-t-2xl"
            )}>
              <img
                src={imageUrl}
                alt={name}
                className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
              />
              <div className={clsx(
                "absolute inset-0",
                highlighted ? "bg-gradient-to-b from-transparent via-black/40 to-black/60" : "bg-gradient-to-b from-black/20 to-transparent"
              )} />

              {highlighted && (
                <div className="absolute top-4 left-4">
                  <Pill
                    text="Highlighted"
                    icon={<BoltIcon className="h-4 w-4" />}
                    className="text-tertiary-base bg-gray-800/70"
                  />
                </div>
              )}
            </div>
          )}

          <div className={clsx(
            "p-6 flex flex-col flex-1 transition-all duration-200 rounded-b-2xl",
            highlighted ? "bg-gray-900 text-white" : "bg-primary-50 group-hover:bg-primary-100/50"
          )}>
            <div className="flex-1 mb-6">
              <h3 className={clsx(
                "text-xl font-bold mb-2",
                highlighted ? "text-white" : "text-gray-900"
              )}>{name}</h3>
              <p className={clsx(
                "line-clamp-3",
                highlighted ? "text-gray-300" : "text-gray-600"
              )}>{description}</p>
            </div>

            <div className="mb-6">
              <div className="flex -space-x-2">
                {displayedMembers.map((member) => (
                  <Avatar
                    key={member.user_id}
                    firstName={member.first_name || ''}
                    lastName={member.last_name || ''}
                    profileImageUrl={member.profile_image_url || undefined}
                    size="small"
                    className="border-2 border-white w-10 h-10"
                  />
                ))}
                {remainingMembers > 0 && (
                  <div className="relative w-10 h-10 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full border-2 border-white">
                    +{remainingMembers}
                  </div>
                )}
              </div>
            </div>

            {workspace && (
              <div className="mb-6 flex items-center gap-2">
                <div className="flex items-center gap-2">
                  {workspace.logo_url && (
                    <Avatar
                      firstName={workspace.workspace_name}
                      profileImageUrl={workspace.logo_url}
                      size="tiny"
                    />
                  )}
                  <Link 
                    to={`/workspace/${workspace.workspace_id}`}
                    className={clsx(
                      "text-sm",
                      highlighted ? "text-gray-200 hover:text-gray-100" : "text-gray-600 hover:text-gray-900"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    by {workspace.workspace_name}
                  </Link>
                </div>
              </div>
            )}

            <div className="mt-auto">
              <Link to={`/communities/${id}`} onClick={handleViewCommunity}>
                <Button
                  color="primary"
                  variant="filled"
                  size="small"
                  className={clsx(
                    "w-fit",
                    highlighted && "border-gray-600 hover:border-gray-500"
                  )}
                >
                  View Community
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </BaseCard>
    </div>
  );
};
